'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircle, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { useMetricToggles } from '../../hooks/strategy';

interface StrategyDynamicHeaderProps {
  params: any;
  metricId?: string;
  availableOptions: Array<{ code: string; label: string }>;
}

/** Threshold above which the inline toggle pills are replaced by a dropdown. */
const DROPDOWN_THRESHOLD = 3;

const StrategyDynamicHeader = ({
  params,
  metricId,
  availableOptions,
}: StrategyDynamicHeaderProps) => {
  const { metricToggles, setMetricToggle } = useMetricToggles();
  const activeMetric =
    metricToggles[metricId || ''] || availableOptions[0]?.code || 'claims';

  const handleToggleClick = (metric: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!metricId) return;
    setMetricToggle(metricId, metric);
  };

  // --- Dropdown state (3+ options) ---
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      triggerRef.current &&
      !triggerRef.current.contains(event.target as Node)
    ) {
      setIsDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, handleClickOutside]);

  // Position the portal-rendered dropdown below the trigger button using
  // CSS custom properties (set on the DOM node) to avoid inline style props.
  const positionDropdown = useCallback(() => {
    if (triggerRef.current && dropdownRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      dropdownRef.current.style.setProperty('--dropdown-top', `${rect.bottom + 4}px`);
      dropdownRef.current.style.setProperty('--dropdown-left', `${rect.left + rect.width / 2}px`);
    }
  }, []);

  const openDropdown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen((prev) => {
      if (!prev) {
        // Position on next frame after portal renders
        requestAnimationFrame(positionDropdown);
      }
      return !prev;
    });
  }, [positionDropdown]);

  const activeLabel =
    availableOptions.find((o) => o.code === activeMetric)?.label ??
    availableOptions[0]?.label ??
    '';

  const useDropdown =
    availableOptions && availableOptions.length >= DROPDOWN_THRESHOLD;

  return (
    <div className="flex flex-col items-start gap-1 py-1">
      <div className="text-xs font-semibold text-left leading-tight truncate max-w-full">
        {params?.colDef?.headerName || ''}
      </div>
      {availableOptions && availableOptions.length > 1 && (
        useDropdown ? (
          /* Dropdown for 3+ options */
          <>
            <button
              ref={triggerRef}
              type="button"
              className="flex items-center gap-1 text-xs text-sky-600 font-semibold cursor-pointer px-1.5 py-0.5 rounded hover:bg-neutral-100 transition-colors"
              onClick={openDropdown}
              data-testid={`metric-dropdown-trigger-${metricId}`}
              aria-haspopup="listbox"
              aria-expanded={isDropdownOpen}
            >
              {activeLabel}
              <Icon icon={faChevronDown} className="text-[0.5rem]" />
            </button>
            {isDropdownOpen && createPortal(
              <div
                ref={(node) => {
                  (dropdownRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
                  // Auto-focus active option on open
                  if (node) {
                    const active = node.querySelector('[aria-selected="true"]') as HTMLElement;
                    active?.focus();
                  }
                }}
                className="fixed w-40 rounded-md shadow-lg bg-white border border-neutral-200 z-[100] py-1 -translate-x-1/2 top-[var(--dropdown-top)] left-[var(--dropdown-left)]"
                role="listbox"
                aria-label="Select display option"
                data-testid={`metric-dropdown-menu-${metricId}`}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsDropdownOpen(false);
                    triggerRef.current?.focus();
                  } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="option"]'));
                    const idx = items.indexOf(e.target as HTMLElement);
                    const next = e.key === 'ArrowDown'
                      ? items[(idx + 1) % items.length]
                      : items[(idx - 1 + items.length) % items.length];
                    next?.focus();
                  }
                }}
              >
                {availableOptions.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    role="option"
                    aria-selected={activeMetric === option.code}
                    className={`w-full text-left px-3 py-1.5 text-xs cursor-pointer transition-colors ${
                      activeMetric === option.code
                        ? 'text-sky-600 font-semibold bg-sky-50'
                        : 'text-neutral-700 hover:bg-neutral-100'
                    }`}
                    onClick={(e) => {
                      handleToggleClick(option.code, e);
                      setIsDropdownOpen(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggleClick(option.code, e as unknown as React.MouseEvent);
                        setIsDropdownOpen(false);
                        triggerRef.current?.focus();
                      }
                    }}
                    data-testid={`metric-dropdown-option-${option.code}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>,
              document.body
            )}
          </>
        ) : (
          /* Inline toggle pills for 2 options */
          <div className="flex items-center gap-1">
            {availableOptions.map((option, index) => (
              <React.Fragment key={option.code}>
                <div
                  className={`text-xs capitalize cursor-pointer px-1 py-0.5 rounded transition-colors ${
                    activeMetric === option.code
                      ? 'text-sky-600 font-semibold'
                      : 'text-neutral-600 hover:bg-neutral-100'
                  }`}
                  onClick={(e) => handleToggleClick(option.code, e)}
                >
                  {option.label}
                </div>
                {index < availableOptions.length - 1 && (
                  <Icon
                    icon={faCircle}
                    className="text-slate-200 text-[0.35rem]"
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default StrategyDynamicHeader;
